// frontend/src/components/boarding/BoardingStatusDashboard.js
import { useState, useEffect, useCallback } from 'react';
import { QrCode, UserPlus, UserX, CheckCircle, Clock, XCircle, RefreshCw, Users, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useTheme } from '../../contexts/ThemeContext';
import { usePreferences } from '../../contexts/PreferenceContext';
import boardingService from '../../services/boardingService';
import translationService from '../../services/translationService';
import notificationSound from '../../utils/notificationSound';
import QRCodeScanner from './QRCodeScanner';
import ManualBoardingModal from './ManualBoardingModal';
import NoShowModal from './NoShowModal';

/**
 * BoardingStatusDashboard
 *
 * Main driver interface for managing passenger boarding
 * Shows all passengers with real-time boarding status
 * Provides quick actions: QR scan, manual board, mark no-show
 */
const BoardingStatusDashboard = ({ tripId, entityId, refreshTrigger = 0, onStatusUpdate }) => {
  const { t, i18n } = useTranslation();
  const { background, text, cn } = useTheme();
  const { entitySettings } = usePreferences();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [boardingStatus, setBoardingStatus] = useState(null);
  const [waypointTranslations, setWaypointTranslations] = useState({});
  const [scannerOpen, setScannerOpen] = useState(false);
  const [manualBoardingOpen, setManualBoardingOpen] = useState(false);
  const [noShowModalOpen, setNoShowModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [boardingMode, setBoardingMode] = useState('manual'); // 'manual' or 'qr_confirm'
  const [qrScanData, setQrScanData] = useState(null); // Store QR data for confirmation

  /**
   * Helper function to get translated waypoint name
   * Follows same pattern as ETADisplay.js
   */
  const getTranslatedWaypointName = (waypointId, originalName) => {
    const defaultLanguage = entitySettings?.entity_default_language || 'en';
    const shouldTranslateContent = entitySettings?.multi_language_enabled === true && i18n.language !== defaultLanguage;

    if (!shouldTranslateContent) {
      return originalName;
    }

    const translation = waypointTranslations[waypointId];
    if (translation?.isTranslated) {
      return translation.content;
    }

    return originalName;
  };

  /**
   * Load boarding status from API
   */
  const loadBoardingStatus = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await boardingService.getTripBoardingStatus(entityId, tripId);

      if (response.success) {
        setBoardingStatus(response.data);
      } else {
        setError(response.error || 'Failed to load boarding status');
      }
    } catch (err) {
      console.error('Error loading boarding status:', err);

      if (err.response?.data?.code === 'BOARDING_TRACKING_DISABLED') {
        setError(t('boardingTrackingDisabled'));
      } else {
        setError(err.response?.data?.error || 'Failed to load boarding status');
      }
    } finally {
      setLoading(false);
    }
  }, [entityId, tripId, t]);

  // Load boarding status on mount and when trip changes
  useEffect(() => {
    if (tripId && entityId) {
      loadBoardingStatus();
    }
  }, [tripId, entityId, refreshTrigger, loadBoardingStatus]);

  // Fetch waypoint translations when boarding status loads or language changes
  useEffect(() => {
    const fetchTranslations = async () => {
      const defaultLanguage = entitySettings?.entity_default_language || 'en';
      const shouldTranslateContent = entitySettings?.multi_language_enabled === true && i18n.language !== defaultLanguage;

      if (!boardingStatus?.bookings || !shouldTranslateContent || !entityId) {
        setWaypointTranslations({});
        return;
      }

      const translations = {};
      const userLanguage = i18n.language;

      // Get all unique waypoint IDs
      const waypointIds = new Set();
      boardingStatus.bookings.forEach(booking => {
        if (booking.pickupStopId) waypointIds.add(booking.pickupStopId);
        if (booking.dropoffStopId) waypointIds.add(booking.dropoffStopId);
      });

      // Fetch translations for each waypoint
      for (const waypointId of waypointIds) {
        try {
          const originalBooking = boardingStatus.bookings.find(
            b => b.pickupStopId === waypointId || b.dropoffStopId === waypointId
          );

          const originalName = originalBooking?.pickupStopId === waypointId
            ? originalBooking.pickup
            : originalBooking.dropoff;

          if (originalName) {
            const translatedContent = await translationService.getTranslatedContent(
              entityId,
              'waypoint',
              waypointId,
              'name',
              userLanguage,
              originalName
            );

            // Store the full translation object, not just the value
            translations[waypointId] = translatedContent;
          }
        } catch (error) {
          console.error(`Error fetching translation for waypoint ${waypointId}:`, error);
        }
      }

      setWaypointTranslations(translations);
    };

    fetchTranslations();
  }, [
    boardingStatus,
    entitySettings?.entity_default_language,
    entitySettings?.multi_language_enabled,
    entityId,
    i18n.language
  ]);

  /**
   * Handle QR code scan success
   * Automatically boards passenger with default rider/luggage counts
   */
  const handleQRScanSuccess = async (qrData) => {
    setScannerOpen(false);

    try {
      // Get current location
      const location = await boardingService.getCurrentLocation();

      // Submit boarding via QR scan (automatic with default counts)
      const response = await boardingService.scanBoardingPass(
        entityId,
        tripId,
        qrData.code,
        null, // Use default rider count from booking
        null, // Use default luggage count from booking
        location
      );

      if (response.success) {
        // Show success toast with sound
        toast.success(t('passengerBoardedSuccessfully') || 'Passenger boarded successfully!');
        notificationSound.play('success');

        // Reload boarding status
        await loadBoardingStatus();

        // Notify parent component
        if (onStatusUpdate) {
          onStatusUpdate(response.data);
        }
      }
    } catch (err) {
      console.error('Boarding error:', err);

      const errorCode = err.response?.data?.code;
      let errorMessage = t('errorGeneratingBoardingPass');

      if (errorCode === 'ALREADY_BOARDED') {
        errorMessage = t('alreadyBoarded');
      } else if (errorCode === 'WRONG_TRIP') {
        errorMessage = t('wrongTrip');
      } else if (errorCode === 'INVALID_BOARDING_PASS') {
        errorMessage = t('invalidBoardingPass');
      }

      toast.error(errorMessage);
      notificationSound.play('error');
    }
  };

  /**
   * Handle QR scan error
   */
  const handleQRScanError = (errorMessage) => {
    setScannerOpen(false);
    setError(errorMessage);
  };

  /**
   * Open manual boarding modal
   * Enriches booking with translated waypoint names before passing to modal
   */
  const handleManualBoard = (booking) => {
    const enrichedBooking = {
      ...booking,
      pickup: getTranslatedWaypointName(booking.pickupStopId, booking.pickup),
      dropoff: getTranslatedWaypointName(booking.dropoffStopId, booking.dropoff)
    };
    setSelectedBooking(enrichedBooking);
    setBoardingMode('manual'); // Set mode to manual boarding
    setQrScanData(null); // Clear any QR data
    setManualBoardingOpen(true);
  };

  /**
   * Handle edit counts for already boarded passengers
   * Opens modal in edit mode with current ridersBoarded and luggageLoaded values
   */
  const handleEditBoardedCounts = (booking) => {
    const enrichedBooking = {
      ...booking,
      pickup: getTranslatedWaypointName(booking.pickupStopId, booking.pickup),
      dropoff: getTranslatedWaypointName(booking.dropoffStopId, booking.dropoff),
      // Use current boarded values instead of original booking values
      bookingRiders: booking.ridersBoarded || booking.bookingRiders,
      bookingLuggages: booking.luggageLoaded || booking.bookingLuggages
    };
    setSelectedBooking(enrichedBooking);
    setBoardingMode('edit'); // Set mode to edit (new mode for editing)
    setQrScanData(null);
    setManualBoardingOpen(true);
  };

  /**
   * Handle manual boarding success (works for manual, QR confirm, and edit modes)
   */
  const handleManualBoardingSuccess = async () => {
    const successMessage = boardingMode === 'edit'
      ? t('countsUpdatedSuccessfully')
      : t('passengerBoardedSuccessfully');

    setManualBoardingOpen(false);
    setSelectedBooking(null);
    setQrScanData(null);
    setBoardingMode('manual');

    toast.success(successMessage);
    notificationSound.play('success');

    await loadBoardingStatus();

    if (onStatusUpdate) {
      onStatusUpdate();
    }
  };

  /**
   * Open no-show modal
   * Enriches booking with translated waypoint names before passing to modal
   */
  const handleMarkNoShow = (booking) => {
    const enrichedBooking = {
      ...booking,
      pickup: getTranslatedWaypointName(booking.pickupStopId, booking.pickup),
      dropoff: getTranslatedWaypointName(booking.dropoffStopId, booking.dropoff)
    };
    setSelectedBooking(enrichedBooking);
    setNoShowModalOpen(true);
  };

  /**
   * Handle no-show success
   */
  const handleNoShowSuccess = async () => {
    setNoShowModalOpen(false);
    setSelectedBooking(null);

    toast.success(t('passengerMarkedAsNoShow') || 'Passenger marked as no-show');

    await loadBoardingStatus();

    if (onStatusUpdate) {
      onStatusUpdate();
    }
  };

  /**
   * Get status icon component
   */
  const getStatusIcon = (status) => {
    switch (status) {
      case 'boarded':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'no_show':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Clock className="h-5 w-5 text-yellow-500" />;
    }
  };

  /**
   * Get status badge
   */
  const getStatusBadge = (status) => {
    switch (status) {
      case 'boarded':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
            <CheckCircle className="h-3 w-3" />
            {t('boarded')}
          </span>
        );
      case 'no_show':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
            <XCircle className="h-3 w-3" />
            {t('noShow')}
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
            <Clock className="h-3 w-3" />
            {t('notBoarded')}
          </span>
        );
    }
  };

  if (loading && !boardingStatus) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error && !boardingStatus) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
        <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
      </div>
    );
  }

  if (!boardingStatus) {
    return null;
  }

  const riderStats = boardingStatus.bookings.reduce(
    (acc, booking) => {
      const bookingRiders = booking.bookingRiders || 0;
      const ridersBoarded = booking.ridersBoarded || 0;

      if (booking.boardingStatus === 'boarded') {
        acc.boarded += ridersBoarded;
        acc.waiting += Math.max(0, bookingRiders - ridersBoarded);
      } else if (booking.boardingStatus === 'no_show') {
        acc.noShow += bookingRiders;
      } else {
        acc.waiting += bookingRiders;
      }

      return acc;
    },
    { boarded: 0, waiting: 0, noShow: 0 }
  );

  /* const boardingProgress = boardingStatus.totalBookings > 0
    ? (boardingStatus.boardedBookings / boardingStatus.totalBookings) * 100
    : 0; */

  return (
    <div>
      {/* Summary Statistics */}
      <div className={cn("rounded-lg shadow p-4 mb-4", background.secondary)}>
        <div className="flex justify-between items-center mb-4">
          <h3 className={cn("text-lg font-semibold", text.primary)}>{t('boardingProgress')}</h3>
          <button
            onClick={loadBoardingStatus}
            className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>

        {/* Progress Bar */}
        {/* <div className="mb-4">
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-2">
            <div
              className={`h-3 rounded-full transition-all duration-500 ${
                boardingProgress === 100 ? 'bg-green-500' : 'bg-blue-500'
              }`}
              style={{ width: `${boardingProgress}%` }}
            ></div>
          </div>
          <p className={cn("text-xs", text.secondary)}>
            {boardingStatus.boardedBookings} / {boardingStatus.totalBookings} {t('boarded')}
          </p>
        </div> */}

        {/* Statistics Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="text-center p-3 rounded bg-gray-50 dark:bg-gray-800">
            <div className="flex justify-center mb-1">
              <Users className="h-5 w-5 text-blue-500" />
            </div>
            <p className={cn("text-2xl font-bold", text.primary)}>{boardingStatus.totalRidersBooked}</p>
            <p className={cn("text-xs", text.secondary)}>{t('totalPassengers')}</p>
          </div>

          <div className="text-center p-3 rounded bg-green-50 dark:bg-green-900/20">
            <div className="flex justify-center mb-1">
              <CheckCircle className="h-5 w-5 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">{riderStats.boarded}</p>
            <p className={cn("text-xs", text.secondary)}>{t('boardedPassengers')}</p>
          </div>

          <div className="text-center p-3 rounded bg-yellow-50 dark:bg-yellow-900/20">
            <div className="flex justify-center mb-1">
              <Clock className="h-5 w-5 text-yellow-500" />
            </div>
            <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{riderStats.waiting}</p>
            <p className={cn("text-xs", text.secondary)}>{t('waitingPassengers')}</p>
          </div>

          <div className="text-center p-3 rounded bg-red-50 dark:bg-red-900/20">
            <div className="flex justify-center mb-1">
              <XCircle className="h-5 w-5 text-red-500" />
            </div>
            <p className="text-2xl font-bold text-red-600 dark:text-red-400">{riderStats.noShow}</p>
            <p className={cn("text-xs", text.secondary)}>{t('noShowPassengers')}</p>
          </div>
        </div>

        {/* Rider Count */}
        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className={cn("text-xs", text.secondary)}>{t('total_riders_booked')}</p>
              <p className={cn("text-lg font-semibold", text.primary)}>{boardingStatus.totalRidersBooked}</p>
            </div>
            <div>
              <p className={cn("text-xs", text.secondary)}>{t('actual_riders_boarded')}</p>
              <p className="text-lg font-semibold text-green-600 dark:text-green-400">{boardingStatus.actualRidersBoarded}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Button */}
      <div className="mb-4">
        <button
          onClick={() => setScannerOpen(true)}
          className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors font-medium"
        >
          <QrCode className="h-5 w-5" />
          {t('scanQRCode')}
        </button>
      </div>

      {/* Passenger List */}
      <div className={cn("rounded-lg shadow", background.secondary)}>
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className={cn("font-semibold", text.primary)}>
            {t('bookings')} ({boardingStatus.bookings.length})
          </h3>
        </div>

        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {boardingStatus.bookings.map((booking) => (
            <div key={booking.bookingId} className="p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  {getStatusIcon(booking.boardingStatus)}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className={cn("font-medium", text.primary)}>{booking.passengerName}</p>
                    {booking.partialBoarding && (
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                        {booking.ridersBoarded}/{booking.bookingRiders}
                      </span>
                    )}
                  </div>
                  <p className={cn("text-sm mb-1", text.secondary)}>
                    {getTranslatedWaypointName(booking.pickupStopId, booking.pickup)} → {getTranslatedWaypointName(booking.dropoffStopId, booking.dropoff)}
                  </p>
                  <p className={cn("text-xs", text.secondary)}>
                    {booking.bookingRiders} {t('riders')}
                    {booking.bookingLuggages > 0 && ` • ${booking.bookingLuggages} ${t('luggage')}`}
                    {booking.boardingMethod && ` • ${t(booking.boardingMethod)}`}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  {getStatusBadge(booking.boardingStatus)}

                  {booking.boardingStatus === 'not_boarded' && (
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleManualBoard(booking)}
                        className="p-2 rounded hover:bg-blue-100 dark:hover:bg-blue-900/20 text-blue-600"
                        title={t('manualBoarding')}
                      >
                        <UserPlus className="h-4 w-4" />
                      </button>

                      <button
                        onClick={() => handleMarkNoShow(booking)}
                        className="p-2 rounded hover:bg-red-100 dark:hover:bg-red-900/20 text-red-600"
                        title={t('markAsNoShow')}
                      >
                        <UserX className="h-4 w-4" />
                      </button>
                    </div>
                  )}

                  {booking.boardingStatus === 'boarded' && (
                    <button
                      onClick={() => handleEditBoardedCounts(booking)}
                      className="px-3 py-1.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      title={t('editCounts')}
                    >
                      {t('edit')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {boardingStatus.bookings.length === 0 && (
          <div className="p-8 text-center">
            <p className={cn("text-sm", text.secondary)}>{t('noBookingsFound')}</p>
          </div>
        )}
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex justify-between items-start">
            <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
            <button
              onClick={() => setError(null)}
              className="text-red-600 hover:text-red-800"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* QR Code Scanner */}
      <QRCodeScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScanSuccess={handleQRScanSuccess}
        onScanError={handleQRScanError}
      />

      {/* Manual Boarding Modal */}
      {selectedBooking && (
        <ManualBoardingModal
          open={manualBoardingOpen}
          onClose={() => {
            setManualBoardingOpen(false);
            setSelectedBooking(null);
            setQrScanData(null);
            setBoardingMode('manual');
          }}
          booking={selectedBooking}
          tripId={tripId}
          entityId={entityId}
          onSuccess={handleManualBoardingSuccess}
          mode={boardingMode}
          qrData={qrScanData}
        />
      )}

      {/* No-Show Modal */}
      {selectedBooking && (
        <NoShowModal
          open={noShowModalOpen}
          onClose={() => {
            setNoShowModalOpen(false);
            setSelectedBooking(null);
          }}
          booking={selectedBooking}
          tripId={tripId}
          entityId={entityId}
          onSuccess={handleNoShowSuccess}
        />
      )}
    </div>
  );
};

export default BoardingStatusDashboard;
